import ButtonWithIcon from 'components/formComponents/Base/ButtonWithIcon';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { cmsApiConfig } from '../../../../../cmsConfig';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import { TerminatedEmployeeInput } from './types';
import { useState } from 'react';

const templateUrl = `${cmsApiConfig.url}/partyApplication/terminatedEmployeesTemplate`;
const exportUrl = `${cmsApiConfig.url}/partyApplication/exportTerminatedEmployees`;
const terminatedEmployeesDownloadFileName =
  'TSL-Uppsagda för ansökan om omställningsstöd.xlsx';

type Props = {
  employees?: TerminatedEmployeeInput[];
  downloadTemplateBtnText?: string;
  downloadCopyBtnText?: string;
  templateFileUrl?: string;
};

const toExportEmployee = (employee: TerminatedEmployeeInput, index: number) => {
  const employmentType = Number(employee.employmentType);
  return {
    rowNumber: index + 1,
    socialSecurityNumber: employee.socialSecurityNumber,
    firstName: employee.firstName,
    lastName: employee.lastName,
    employmentType: Number.isFinite(employmentType) ? employmentType : null,
    startDate: employee.startDate,
    terminationDate: employee.terminationDate,
    endDate: employee.endDate,
    email: employee.email,
    phone: employee.phone,
  };
};

const TemplateDownloadButton = ({
  employees = [],
  downloadTemplateBtnText = 'Ladda ner mall',
  downloadCopyBtnText = 'Ladda ner kopia',
  templateFileUrl,
}: Props) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const resolvedTemplateUrl = templateFileUrl || templateUrl;

  const downloadBlob = (blob: Blob, fileName: string) => {
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleDownload = async () => {
    if (employees.length === 0) {
      setIsDownloading(true);
      try {
        const response = await fetch(resolvedTemplateUrl);
        if (!response.ok) {
          throw new Error(
            `Template download failed with status ${response.status}`
          );
        }

        const blob = await response.blob();
        downloadBlob(blob, terminatedEmployeesDownloadFileName);
      } catch (error) {
        console.error('Could not download template file', error);
        window.open(resolvedTemplateUrl, '_self');
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(exportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(employees.map(toExportEmployee)),
      });

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }

      const blob = await response.blob();
      downloadBlob(blob, terminatedEmployeesDownloadFileName);
    } catch (error) {
      // Keep the flow usable if export fails for any reason.
      console.error('Could not export terminated employees list', error);
      window.open(resolvedTemplateUrl, '_self');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.toolbarAction}>
      <ButtonWithIcon
        type="button"
        label={
          employees.length === 0 ? downloadTemplateBtnText : downloadCopyBtnText
        }
        Icon={DownloadOutlinedIcon}
        onClick={() => {
          void handleDownload();
        }}
        disabled={isDownloading}
        dataCy={CYPRESS_DATA.templateLink}
      />
    </div>
  );
};

export default TemplateDownloadButton;
